// Comercial's pure logic -- testable with node (comercialLogica.test.mjs).
// Comercial keeps its own helpers (menu, functions, errors, dates) so it never
// depends on another module's code (OT, Mantenimiento).

// May the user exercise this Comercial function? (comercial_funciones in /auth/me)
export const puede = (user, funcion) => (user?.comercial_funciones || []).includes(funcion)

// The Comercial side menu: groups of { to, clave, icono, funcion }, only what
// the user's functions allow; a group left without items is not shown.
// Layout turns `clave` into a label (nav:items.<clave>) and `icono` into an icon.
const MENU = [
  { grupo: 'crm', items: [
    { to: '/comercial/organizaciones', clave: 'comercialOrganizaciones', icono: 'Building', funcion: 'comercial_ver' },
    { to: '/comercial/contactos',      clave: 'comercialContactos',      icono: 'Users',    funcion: 'comercial_ver' },
    // Oportunidades goes here when it exists (E4-3: hidden until then).
  ] },
  { grupo: 'adminComercial', items: [
    { to: '/comercial/admin/tipos',     clave: 'comercialTipos',     icono: 'Briefcase', funcion: 'comercial_configurar' },
    { to: '/comercial/admin/etiquetas', clave: 'comercialEtiquetas', icono: 'Tag',      funcion: 'comercial_configurar' },
    { to: '/comercial/admin/permisos',  clave: 'comercialPermisos',  icono: 'Key',      funcion: 'comercial_permisos' },
  ] },
]

export function navComercial(user) {
  return MENU
    .map(g => ({ grupo: g.grupo, items: g.items.filter(i => puede(user, i.funcion)) }))
    .filter(g => g.items.length)
}

// Where /comercial opens: the first page the user may see (null = none).
export function inicioComercial(user) {
  return navComercial(user)[0]?.items[0]?.to || null
}

// A backend error as a message. Comercial answers {code, message, ...extra}:
// a known code takes its text from comercial.json (errores.<code>, with the
// extra fields to interpolate); an unknown one shows the server's message.
//   t: i18next's t (comercial namespace); generico: the fallback text
export function mensajeError(err, t, generico) {
  const d = err?.response?.data?.detail
  if (d && typeof d === 'object' && !Array.isArray(d) && d.code) {
    const { code, message, ...extra } = d
    const traducido = t(`errores.${code}`, { ...extra, defaultValue: '' })
    return traducido || message || generico
  }
  if (typeof d === 'string' && d) return d
  return generico
}

// The duplicates a 409 posibles_duplicados brings (null for any other error).
export function coincidenciasDuplicado(err) {
  const d = err?.response?.data?.detail
  return err?.response?.status === 409 && d?.code === 'posibles_duplicados' ? (d.coincidencias || []) : null
}

// A catalogue (types, labels) in its shown order: by `orden`, then id.
export const ordenarCatalogo = (items) =>
  [...(items || [])].sort((a, b) => (a.orden - b.orden) || (a.id - b.id))

// Moving an item one place up (-1) or down (+1): the catalogue is renumbered
// 1..n in its new order, which also mends gaps and repeated numbers. Returns
// only the changes to send, [{ id, orden }] ([] at either end or for an unknown id).
export function reordenar(items, id, dir) {
  const lista = ordenarCatalogo(items)
  const i = lista.findIndex(x => x.id === id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= lista.length) return []
  ;[lista[i], lista[j]] = [lista[j], lista[i]]
  return lista
    .map((x, k) => ({ id: x.id, orden: k + 1, antes: x.orden }))
    .filter(x => x.orden !== x.antes)
    .map(({ id, orden }) => ({ id, orden }))
}

// A date (YYYY-MM-DD) or a timestamp in the user's locale. A bare date is
// built from its parts: new Date('2026-10-28') reads UTC midnight and shows the
// previous day in Chile.
export function formatFecha(iso, locale) {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
  return isNaN(d) ? '' : d.toLocaleDateString(locale)
}

// The open record card, from the address: ?org=<id> or ?contacto=<id>
// (one at a time). ?org=nueva is a new organization's form, maybe under
// ?superior=<id> ("+ Suborganización"); ?contacto=nueva a new contact's,
// maybe already linked to ?desde=<org id> (created from that organization's card). null = none.
export function fichaDeParams(params) {
  const id = (k) => { const n = Number(params.get(k)); return n > 0 ? n : null }
  if (params.get('org') === 'nueva') return { tipo: 'org', id: 'nueva', superior: id('superior') }
  if (id('org')) return { tipo: 'org', id: id('org') }
  if (params.get('contacto') === 'nueva') return { tipo: 'contacto', id: 'nueva', desde: id('desde') }
  if (id('contacto')) return { tipo: 'contacto', id: id('contacto') }
  return null
}

// The address params with this card open (or none, ficha = null), keeping the
// rest (filters, search).
export function paramsConFicha(params, ficha) {
  const next = new URLSearchParams(params)
  for (const k of ['org', 'contacto', 'superior', 'desde']) next.delete(k)
  if (ficha) next.set(ficha.tipo, String(ficha.id))
  if (ficha?.superior) next.set('superior', String(ficha.superior))
  if (ficha?.desde) next.set('desde', String(ficha.desde))
  return next
}
